import React from 'react'
import { Navigate } from 'react-router-dom'
import { useSubscriptionFeatures } from '../hooks/useSubscriptionFeatures'

interface TrainerRouteGuardProps {
  children: React.ReactNode
  blockTrainers?: boolean
  redirectTo?: string
}

/**
 * Route guard that prevents trainers from accessing facility-specific features
 * or redirects non-trainers from trainer-specific features
 */
export default function TrainerRouteGuard({ 
  children, 
  blockTrainers = true,
  redirectTo = '/'
}: TrainerRouteGuardProps) {
  const { isTrainerMode, isLoading } = useSubscriptionFeatures()

  // Show loading state while checking subscription
  if (isLoading) {
    return (
      <div className="min-h-screen bg-dark-primary text-text-primary flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gold mx-auto mb-4"></div>
          <p className="text-text-secondary">Loading...</p>
        </div>
      </div>
    )
  }

  // Block trainers from facility features
  if (blockTrainers && isTrainerMode) {
    return <Navigate to={redirectTo} replace />
  }

  // Block non-trainers from trainer features
  if (!blockTrainers && !isTrainerMode) {
    return <Navigate to={redirectTo} replace />
  }

  // Render children if checks pass
  return <>{children}</>
}

// Convenience wrapper for facility-only routes
export function FacilityOnlyRoute({ children }: { children: React.ReactNode }) {
  return (
    <TrainerRouteGuard blockTrainers={true}>
      {children}
    </TrainerRouteGuard>
  )
}

// Convenience wrapper for trainer-only routes
export function TrainerOnlyRoute({ children }: { children: React.ReactNode }) {
  return (
    <TrainerRouteGuard blockTrainers={false}>
      {children}
    </TrainerRouteGuard>
  )
}